// AutomationDashboard.js
// Full-featured Automation & Analytics Dashboard
// Tabs: Overview · Pipeline · AI · Workflows · Activity · Settings
// JobEng · business/recruitment/

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  RefreshControl, ActivityIndicator, Modal, TextInput,
  Switch, FlatList, Alert, Platform, StatusBar, Dimensions,
  KeyboardAvoidingView,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width, height } = Dimensions.get('window');
const API_BASE  = 'https://api.jobeng.ai';
const API_KEY   = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const CACHE_KEY = 'automation_dash_v2';
const REFRESH_MS = 30_000;

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'overview',   label: 'Overview',   icon: 'view-dashboard'         },
  { id: 'pipeline',   label: 'Pipeline',   icon: 'transit-connection'     },
  { id: 'ai',         label: 'AI',         icon: 'robot'                  },
  { id: 'workflows',  label: 'Workflows',  icon: 'cog-transfer'           },
  { id: 'activity',   label: 'Activity',   icon: 'timeline-text'          },
  { id: 'settings',   label: 'Settings',   icon: 'tune'                   },
];

const PERIODS = ['Today', '7 Days', '30 Days', 'All Time'];

const STAGES = [
  { id: 'job-posting',         title: 'Job Postings',    icon: 'briefcase-outline',  color: '#4A90E2' },
  { id: 'applicants',          title: 'Applicants',       icon: 'account-multiple',   color: '#50C878' },
  { id: 'applicant-selection', title: 'AI Shortlisted',  icon: 'account-check',      color: '#FFB84D' },
  { id: 'test-candidates',     title: 'Testing',          icon: 'clipboard-check',    color: '#9B59B6' },
  { id: 'video-interview',     title: 'Video Interview',  icon: 'video',              color: '#E74C3C' },
  { id: 'qualified-candidates','title':'AI Qualified',   icon: 'account-star',       color: '#3498DB' },
  { id: 'final-interview',     title: 'Final Interview',  icon: 'account-tie',        color: '#1ABC9C' },
  { id: 'job-offers',          title: 'Job Offers',       icon: 'email-arrow-right',  color: '#F39C12' },
  { id: 'accepted-offers',     title: 'Accepted Offers',  icon: 'check-circle',       color: '#27AE60' },
  { id: 'hired',               title: 'Hired',            icon: 'trophy',             color: '#8E44AD' },
];

const AI_AGENTS = [
  { id: 'cv-scorer',       name: 'CV Scorer',          icon: 'file-account',       color: '#3B82F6', desc: 'Scores CVs against job requirements'        },
  { id: 'skills-matcher',  name: 'Skills Matcher',     icon: 'code-braces',        color: '#10B981', desc: 'Matches candidate skills to job needs'       },
  { id: 'exp-analyser',    name: 'Experience Analyser',icon: 'briefcase-clock',    color: '#F59E0B', desc: 'Validates and weighs experience relevance'   },
  { id: 'culture-fit',     name: 'Culture Fit AI',     icon: 'account-heart',      color: '#EC4899', desc: 'Predicts cultural alignment score'           },
  { id: 'bias-detector',   name: 'Bias Detector',      icon: 'shield-check',       color: '#8B5CF6', desc: 'Ensures fair, unbiased shortlisting'         },
  { id: 'interview-prep',  name: 'Interview Prep',     icon: 'comment-question',   color: '#06B6D4', desc: 'Generates tailored interview questions'      },
  { id: 'offer-optimiser', name: 'Offer Optimiser',    icon: 'currency-gbp',       color: '#EF4444', desc: 'Recommends competitive offer packages'       },
  { id: 'retention-pred',  name: 'Retention Predictor',icon: 'chart-timeline-variant',color:'#F97316',desc: 'Predicts 12-month retention probability'   },
];

const RULE_TRIGGERS = [
  'New application received', 'AI score calculated', 'Candidate shortlisted',
  'Test completed', 'Video interview done', 'Final interview scheduled',
  'Offer sent', 'Offer accepted', 'Candidate rejected', 'Stage moved manually',
];
const RULE_ACTIONS = [
  'Send email to candidate', 'Send email to recruiter', 'Move to next stage',
  'Schedule interview', 'Create task', 'Post Slack notification',
  'Add tag to candidate', 'Update candidate score', 'Archive candidate',
  'Trigger AI re-score',
];
const RULE_CONDITIONS = [
  'AI score > threshold', 'AI score < threshold', 'Experience > X years',
  'Location matches', 'Skills match > %', 'No activity for X days',
  'Stage is specific', 'Tag is set', 'Any time', 'Business hours only',
];

const ACTIVITY_ICONS = {
  'ai-score':    { icon: 'brain',               color: '#8B5CF6' },
  'stage-move':  { icon: 'transfer-right',       color: '#3B82F6' },
  'hired':       { icon: 'trophy',               color: '#10B981' },
  'rejected':    { icon: 'account-remove',       color: '#EF4444' },
  'interview':   { icon: 'calendar-check',       color: '#F59E0B' },
  'offer':       { icon: 'email-arrow-right',    color: '#06B6D4' },
  'note':        { icon: 'note-text',            color: '#6B7280' },
  'applied':     { icon: 'account-plus',         color: '#0e4e96' },
  'rule-fired':  { icon: 'cog-play',             color: '#EC4899' },
  'sync':        { icon: 'cloud-sync',           color: '#34D399' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const pct   = (a, b) => b ? Math.round((a / b) * 100) : 0;
const sc    = (s) => s >= 80 ? '#10B981' : s >= 65 ? '#F59E0B' : s >= 50 ? '#F97316' : '#EF4444';
const fmtN  = (n) => n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n||0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short'}) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'}) : '';
const fmtAgo  = (d) => {
  if (!d) return '—';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)   return 'just now';
  if (m < 60)  return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m/60)}h ago`;
  return fmtDate(d);
};

const mapStage = (s) => {
  const m = {
    'new':'applicants','applied':'applicants','pending':'applicants',
    'shortlisted':'applicant-selection','screened':'applicant-selection',
    'testing':'test-candidates','assessment':'test-candidates',
    'video':'video-interview','qualified':'qualified-candidates',
    'interview':'final-interview','final-interview':'final-interview',
    'offer':'job-offers','accepted':'accepted-offers','hired':'hired',
  };
  return m[s?.toLowerCase()] || s || 'applicants';
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AutomationDashboard = ({ navigation }) => {
  const [tab,        setTab]        = useState('overview');
  const [period,     setPeriod]     = useState('30 Days');
  const [companyId,  setCompanyId]  = useState(null);
  const [authToken,  setAuthToken]  = useState(null);

  // Data
  const [applications, setApplications] = useState([]);
  const [jobs,         setJobs]         = useState([]);
  const [rules,        setRules]        = useState([]);
  const [activity,     setActivity]     = useState([]);
  const [agentStats,   setAgentStats]   = useState({});
  const [lastSync,     setLastSync]     = useState(null);

  // UI
  const [isLoading,    setIsLoading]    = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [processing,   setProcessing]   = useState('');

  // Modals
  const [ruleModal,    setRuleModal]    = useState(false);
  const [editRule,     setEditRule]     = useState(null);
  const [agentModal,   setAgentModal]   = useState(false);
  const [selAgent,     setSelAgent]     = useState(null);
  const [exportModal,  setExportModal]  = useState(false);

  // Settings state
  const [settings, setSettings] = useState({
    autoProgress:       false,
    autoProgressScore:  75,
    aiAutoScore:        true,
    emailCandidates:    true,
    emailRecruiter:     true,
    slackNotify:        false,
    shortlistThreshold: 70,
    rejectThreshold:    40,
    syncInterval:       5,
    retentionDays:      90,
  });

  // Rule form
  const [ruleForm, setRuleForm] = useState({ name:'', trigger:'', condition:'', action:'', threshold:'75', active:true });

  const timerRef = useRef(null);

  // ─── Init ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    bootstrap();
    timerRef.current = setInterval(() => loadData(null, null, true), REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const bootstrap = async () => {
    const [cid, tok] = await Promise.all([
      AsyncStorage.getItem('company_id') || AsyncStorage.getItem('business_id'),
      AsyncStorage.getItem('auth_token') || AsyncStorage.getItem('userToken'),
    ]);
    setCompanyId(cid || '1');
    setAuthToken(tok);
    await loadData(cid || '1', tok);
  };

  const hdrs = (tok) => ({
    'X-API-Key': API_KEY,
    'Authorization': `Bearer ${tok}`,
    'Content-Type': 'application/json',
  });

  const loadData = async (cid, tok, silent = false) => {
    const id  = cid  || companyId  || '1';
    const tkn = tok  || authToken;
    if (!silent) setIsLoading(true);
    try {
      const [appsR, jobsR, statsR, rulesR, actR, settR] = await Promise.allSettled([
        axios.get(`${API_BASE}/api/data/applications?company_id=${id}&limit=1000`, { headers: hdrs(tkn) }),
        axios.get(`${API_BASE}/api/data/jobs?company_id=${id}&limit=200`,           { headers: hdrs(tkn) }),
        axios.get(`${API_BASE}/api/recruitment/pipeline/stats?company_id=${id}`,    { headers: hdrs(tkn) }),
        axios.get(`${API_BASE}/api/recruitment/automation/rules?company_id=${id}`,  { headers: hdrs(tkn) }),
        axios.get(`${API_BASE}/api/recruitment/activity?company_id=${id}&limit=100`,{ headers: hdrs(tkn) }),
        axios.get(`${API_BASE}/api/recruitment/automation/settings?company_id=${id}`,{ headers: hdrs(tkn) }),
      ]);

      const apps  = appsR.status  === 'fulfilled' ? (appsR.value.data?.data  || appsR.value.data  || []) : [];
      const jbs   = jobsR.status  === 'fulfilled' ? (jobsR.value.data?.data  || jobsR.value.data  || []) : [];
      const rls   = rulesR.status === 'fulfilled' ? (rulesR.value.data?.data || rulesR.value.data || []) : [];
      const acts  = actR.status   === 'fulfilled' ? (actR.value.data?.data   || actR.value.data   || []) : [];
      const setts = settR.status  === 'fulfilled' ? (settR.value.data?.settings || {}) : {};

      // Derive agent stats from applications
      const agSt = {};
      AI_AGENTS.forEach(ag => {
        const scored = apps.filter(a => a.ai_score !== null && a.ai_score !== undefined);
        agSt[ag.id] = {
          processed: scored.length,
          avgScore:  scored.length ? Math.round(scored.reduce((s,a)=>s+(a.ai_score||a.score||0),0)/scored.length) : null,
          enabled:   true,
        };
      });

      await AsyncStorage.setItem(CACHE_KEY, JSON.stringify({ apps, jbs, rls, acts, ts: Date.now() }));

      setApplications(apps);
      setJobs(jbs);
      setRules(rls);
      setActivity(acts);
      setAgentStats(agSt);
      if (Object.keys(setts).length) setSettings(s => ({ ...s, ...setts }));
      setLastSync(new Date());
    } catch {
      try {
        const c = JSON.parse(await AsyncStorage.getItem(CACHE_KEY) || '{}');
        if (c.apps) { setApplications(c.apps); setJobs(c.jbs||[]); setRules(c.rls||[]); setActivity(c.acts||[]); }
      } catch {}
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // ─── Derived metrics ──────────────────────────────────────────────────────
  const metrics = useCallback(() => {
    const filterByPeriod = (arr) => {
      const now = Date.now();
      const daysMap = { 'Today': 1, '7 Days': 7, '30 Days': 30, 'All Time': 99999 };
      const days = daysMap[period] || 30;
      return arr.filter(a => (now - new Date(a.applied_at||a.created_at||0).getTime()) < days * 86400000);
    };

    const apps    = filterByPeriod(applications);
    const active  = apps.filter(a => a.status !== 'rejected');
    const rejected= apps.filter(a => a.status === 'rejected').length;
    const scored  = active.filter(a => (a.ai_score||a.score) !== null && (a.ai_score||a.score) !== undefined);
    const avgScore= scored.length ? Math.round(scored.reduce((s,a)=>s+(a.ai_score||a.score||0),0)/scored.length) : null;

    const stageMap = {};
    STAGES.forEach(st => { stageMap[st.id] = 0; });
    active.forEach(a => {
      const stage = mapStage(a.status || a.pipeline_stage);
      if (stageMap[stage] !== undefined) stageMap[stage]++;
    });

    const hired    = stageMap['hired'] || 0;
    const offered  = (stageMap['job-offers']||0) + (stageMap['accepted-offers']||0);
    const total    = active.length || 1;

    // Time-to-hire estimate
    const hiredApps = apps.filter(a => a.status==='hired' && a.applied_at && a.updated_at);
    const avgTTH = hiredApps.length
      ? Math.round(hiredApps.reduce((s,a)=>s+(new Date(a.updated_at)-new Date(a.applied_at))/(86400000),0)/hiredApps.length)
      : null;

    // Score distribution
    const scoreDist = [
      { label:'90+',  count: scored.filter(a=>(a.ai_score||a.score)>=90).length,   color:'#10B981' },
      { label:'75-89',count: scored.filter(a=>{const sc=a.ai_score||a.score; return sc>=75&&sc<90;}).length, color:'#34D399' },
      { label:'60-74',count: scored.filter(a=>{const sc=a.ai_score||a.score; return sc>=60&&sc<75;}).length, color:'#F59E0B' },
      { label:'45-59',count: scored.filter(a=>{const sc=a.ai_score||a.score; return sc>=45&&sc<60;}).length, color:'#F97316' },
      { label:'<45',  count: scored.filter(a=>(a.ai_score||a.score)<45).length,    color:'#EF4444' },
    ];

    // Source breakdown
    const srcMap = {};
    apps.forEach(a => { const s = a.application_source||a.source||'Direct'; srcMap[s]=(srcMap[s]||0)+1; });

    // Active rules count
    const activeRules = rules.filter(r => r.active||r.enabled).length;
    const rulesRan = rules.reduce((s,r) => s+(r.execution_count||r.times_fired||0), 0);

    return {
      total: active.length, hired, offered, rejected,
      shortlisted: stageMap['applicant-selection'] || 0,
      testing:     stageMap['test-candidates'] || 0,
      qualified:   stageMap['qualified-candidates'] || 0,
      avgScore, stageMap, scoreDist, srcMap,
      hiringRate:      pct(hired, total),
      offerRate:       pct(offered, total),
      shortlistRate:   pct(stageMap['applicant-selection']||0, stageMap['applicants']||total),
      qualRate:        pct(stageMap['qualified-candidates']||0, stageMap['test-candidates']||1),
      conversionRate:  pct(hired, total),
      avgTimeToHire:   avgTTH,
      activeJobs:      jobs.filter(j => j.status==='active'||!j.status).length,
      activeRules, rulesRan,
      apps,
    };
  }, [applications, jobs, rules, period]);

  const M = metrics();

  // ─── API mutations ────────────────────────────────────────────────────────
  const saveRule = async () => {
    if (!ruleForm.name.trim() || !ruleForm.trigger || !ruleForm.action) {
      Alert.alert('Required', 'Please fill in name, trigger and action.'); return;
    }
    setProcessing('rule');
    try {
      const body = { ...ruleForm, company_id: parseInt(companyId)||0, threshold: parseFloat(ruleForm.threshold)||75 };
      if (editRule) {
        const res = await axios.put(`${API_BASE}/api/recruitment/automation/rules/${editRule.id}`, body, { headers: hdrs(authToken) });
        setRules(r => r.map(x => x.id===editRule.id ? (res.data?.rule||body) : x));
      } else {
        const res = await axios.post(`${API_BASE}/api/recruitment/automation/rules`, body, { headers: hdrs(authToken) });
        setRules(r => [...r, res.data?.rule || { ...body, id: Date.now(), execution_count:0 }]);
      }
      setRuleModal(false); setEditRule(null);
      setRuleForm({ name:'', trigger:'', condition:'', action:'', threshold:'75', active:true });
    } catch { Alert.alert('Error', 'Could not save rule.'); }
    finally { setProcessing(''); }
  };

  const toggleRule = async (rule) => {
    try {
      const updated = { ...rule, active: !rule.active };
      await axios.put(`${API_BASE}/api/recruitment/automation/rules/${rule.id}`, updated, { headers: hdrs(authToken) });
      setRules(r => r.map(x => x.id===rule.id ? updated : x));
    } catch { Alert.alert('Error', 'Could not toggle rule.'); }
  };

  const deleteRule = (rule) => {
    Alert.alert('Delete Rule', `Delete "${rule.name}"?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        try {
          await axios.delete(`${API_BASE}/api/recruitment/automation/rules/${rule.id}`, { headers: hdrs(authToken) });
          setRules(r => r.filter(x => x.id !== rule.id));
        } catch { Alert.alert('Error', 'Could not delete rule.'); }
      }},
    ]);
  };

  const runAI = async () => {
    setProcessing('ai');
    try {
      await axios.post(`${API_BASE}/api/recruitment/ai-process`, { company_id: parseInt(companyId)||0 }, { headers: hdrs(authToken) });
      await loadData(null, null, true);
      Alert.alert('🤖 Done', 'AI scoring completed for all candidates.');
    } catch { Alert.alert('Error', 'AI processing failed.'); }
    finally { setProcessing(''); }
  };

  const runAutoProgress = async () => {
    setProcessing('progress');
    try {
      await axios.post(`${API_BASE}/api/recruitment/auto-progress`, { company_id: parseInt(companyId)||0, threshold: settings.autoProgressScore }, { headers: hdrs(authToken) });
      await loadData(null, null, true);
      Alert.alert('✅ Done', `Candidates above ${settings.autoProgressScore}% moved automatically.`);
    } catch { Alert.alert('Error', 'Auto-progression failed.'); }
    finally { setProcessing(''); }
  };

  const saveSettings = async () => {
    setProcessing('settings');
    try {
      await axios.put(`${API_BASE}/api/recruitment/automation/settings`, { company_id: parseInt(companyId)||0, ...settings }, { headers: hdrs(authToken) });
      Alert.alert('✅ Saved', 'Settings saved successfully.');
    } catch { Alert.alert('Error', 'Could not save settings.'); }
    finally { setProcessing(''); }
  };

  const toggleAgentEnabled = (agentId) => {
    setAgentStats(prev => ({
      ...prev,
      [agentId]: { ...prev[agentId], enabled: !prev[agentId]?.enabled },
    }));
  };

  // ─── Render: Tab bar ──────────────────────────────────────────────────────
  const renderTabBar = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.tabBar} contentContainerStyle={{ paddingHorizontal:8 }}>
      {TABS.map(t => (
        <TouchableOpacity key={t.id} style={[s.tab, tab===t.id && s.tabActive]} onPress={() => setTab(t.id)}>
          <MaterialCommunityIcons name={t.icon} size={16} color={tab===t.id ? '#0e4e96' : '#9CA3AF'} />
          <Text style={[s.tabText, tab===t.id && s.tabTextActive]}>{t.label}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  // ─── Render: Header ───────────────────────────────────────────────────────
  const renderHeader = () => (
    <LinearGradient colors={['#0e4e96','#1e7be2']} style={s.header}>
      <StatusBar barStyle="light-content" />
      <View style={s.headerRow}>
        <TouchableOpacity style={s.iconBtn} onPress={() => navigation.goBack()}>
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFF" />
        </TouchableOpacity>
        <View style={{ flex:1, marginLeft:12 }}>
          <Text style={s.headerTitle}>Automation Dashboard</Text>
          <Text style={s.headerSub}>{lastSync ? `Synced ${fmtTime(lastSync)}` : 'Loading…'} · {M.total} candidates</Text>
        </View>
        <TouchableOpacity style={s.iconBtn} onPress={() => setExportModal(true)}>
          <MaterialCommunityIcons name="export-variant" size={20} color="#FFF" />
        </TouchableOpacity>
        <TouchableOpacity style={[s.iconBtn,{marginLeft:6}]} onPress={() => navigation.navigate('AutomatedHiringPipelineScreen')}>
          <MaterialCommunityIcons name="pipe" size={20} color="#FFF" />
        </TouchableOpacity>
      </View>

      {/* Period selector */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop:12 }}>
        {PERIODS.map(p => (
          <TouchableOpacity key={p} style={[s.periodBtn, period===p && s.periodBtnActive]} onPress={() => setPeriod(p)}>
            <Text style={[s.periodText, period===p && s.periodTextActive]}>{p}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </LinearGradient>
  );

  // ─── TAB: Overview ────────────────────────────────────────────────────────
  const renderOverview = () => (
    <ScrollView
      refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={() => { setIsRefreshing(true); loadData(null,null,true); }} colors={['#0e4e96']} />}
      contentContainerStyle={{ padding:16, paddingBottom:100 }}
    >
      {/* Primary KPI row */}
      <View style={s.kpiRow}>
        {[
          { label:'Applicants', val:M.total,       icon:'account-multiple',  color:'#3B82F6' },
          { label:'Hired',      val:M.hired,        icon:'trophy',            color:'#10B981' },
          { label:'Hire Rate',  val:`${M.hiringRate}%`, icon:'percent',      color:'#8B5CF6' },
          { label:'Avg Score',  val: M.avgScore !== null ? `${M.avgScore}%` : '—', icon:'brain', color:'#EC4899' },
        ].map(k => (
          <View key={k.label} style={[s.kpiCard, { borderTopColor: k.color }]}>
            <MaterialCommunityIcons name={k.icon} size={22} color={k.color} />
            <Text style={s.kpiVal}>{k.val}</Text>
            <Text style={s.kpiLabel}>{k.label}</Text>
          </View>
        ))}
      </View>

      {/* Secondary KPI row */}
      <View style={s.kpiRow}>
        {[
          { label:'Offered',     val:M.offered,      icon:'email-arrow-right',  color:'#F59E0B' },
          { label:'Rejected',    val:M.rejected,     icon:'account-remove',     color:'#EF4444' },
          { label:'Active Jobs', val:M.activeJobs,   icon:'briefcase-outline',  color:'#06B6D4' },
          { label:'Rules Active',val:M.activeRules,  icon:'cog-play',           color:'#F97316' },
        ].map(k => (
          <View key={k.label} style={[s.kpiCard, { borderTopColor: k.color }]}>
            <MaterialCommunityIcons name={k.icon} size={22} color={k.color} />
            <Text style={s.kpiVal}>{fmtN(k.val)}</Text>
            <Text style={s.kpiLabel}>{k.label}</Text>
          </View>
        ))}
      </View>

      {/* Quick metrics */}
      <View style={s.card}>
        <Text style={s.cardTitle}>Conversion Metrics</Text>
        {[
          { label:'Shortlisting Rate',  val: M.shortlistRate,   color:'#3B82F6' },
          { label:'Qualification Rate', val: M.qualRate,        color:'#10B981' },
          { label:'Offer Rate',         val: M.offerRate,       color:'#F59E0B' },
          { label:'Overall Hire Rate',  val: M.hiringRate,      color:'#8B5CF6' },
        ].map(m => (
          <View key={m.label} style={s.metricRow}>
            <Text style={s.metricLabel}>{m.label}</Text>
            <View style={s.metricBarBg}>
              <View style={[s.metricBarFill, { width:`${m.val}%`, backgroundColor:m.color }]} />
            </View>
            <Text style={[s.metricVal, { color:m.color }]}>{m.val}%</Text>
          </View>
        ))}
        {M.avgTimeToHire !== null && (
          <View style={[s.metricRow, { marginTop:10, paddingTop:10, borderTopWidth:1, borderTopColor:'#F3F4F6' }]}>
            <MaterialCommunityIcons name="clock-fast" size={16} color="#6B7280" />
            <Text style={[s.metricLabel, { marginLeft:8 }]}>Avg Time to Hire</Text>
            <Text style={[s.metricVal, { color:'#0e4e96' }]}>{M.avgTimeToHire} days</Text>
          </View>
        )}
      </View>

      {/* Mini funnel */}
      <View style={s.card}>
        <Text style={s.cardTitle}>Pipeline Funnel</Text>
        {STAGES.slice(1).map(st => {
          const cnt  = M.stageMap[st.id] || 0;
          const barW = M.total ? pct(cnt, M.total) : 0;
          return (
            <View key={st.id} style={s.funnelRow}>
              <MaterialCommunityIcons name={st.icon} size={14} color={st.color} style={{ width:20 }} />
              <Text style={s.funnelLabel} numberOfLines={1}>{st.title}</Text>
              <View style={s.funnelBarBg}>
                <View style={[s.funnelBarFill, { width:`${barW}%`, backgroundColor:st.color }]} />
              </View>
              <Text style={s.funnelCount}>{cnt}</Text>
            </View>
          );
        })}
      </View>

      {/* Score distribution */}
      <View style={s.card}>
        <Text style={s.cardTitle}>AI Score Distribution</Text>
        <View style={s.distribRow}>
          {M.scoreDist.map(d => {
            const maxCnt = Math.max(...M.scoreDist.map(x=>x.count), 1);
            return (
              <View key={d.label} style={s.distribCol}>
                <View style={s.distribBarBg}>
                  <View style={[s.distribBarFill, { height:`${pct(d.count,maxCnt)}%`, backgroundColor:d.color }]} />
                </View>
                <Text style={s.distribNum}>{d.count}</Text>
                <Text style={s.distribLabel}>{d.label}</Text>
              </View>
            );
          })}
        </View>
      </View>

      {/* Source breakdown */}
      <View style={s.card}>
        <Text style={s.cardTitle}>Application Sources</Text>
        {Object.entries(M.srcMap).map(([src, cnt]) => (
          <View key={src} style={s.funnelRow}>
            <MaterialCommunityIcons name="source-branch" size={14} color="#0e4e96" style={{ width:20 }} />
            <Text style={s.funnelLabel}>{src}</Text>
            <View style={s.funnelBarBg}>
              <View style={[s.funnelBarFill, { width:`${pct(cnt, M.total||1)}%`, backgroundColor:'#0e4e96' }]} />
            </View>
            <Text style={s.funnelCount}>{cnt}</Text>
          </View>
        ))}
        {Object.keys(M.srcMap).length === 0 && <Text style={s.emptyText}>No source data yet</Text>}
      </View>

      {/* Recent activity preview */}
      <View style={s.card}>
        <View style={s.cardTitleRow}>
          <Text style={s.cardTitle}>Recent Activity</Text>
          <TouchableOpacity onPress={() => setTab('activity')}>
            <Text style={s.viewAll}>View all →</Text>
          </TouchableOpacity>
        </View>
        {activity.slice(0,5).map((act, i) => renderActivityItem(act, i))}
        {activity.length === 0 && <Text style={s.emptyText}>No recent activity</Text>}
      </View>

      {/* Quick actions */}
      <View style={s.card}>
        <Text style={s.cardTitle}>Quick Actions</Text>
        <View style={s.quickGrid}>
          {[
            { label:'Run AI Scoring',    icon:'robot',           color:'#8B5CF6', action: runAI,          key:'ai'       },
            { label:'Auto-Progress',     icon:'fast-forward',    color:'#10B981', action: runAutoProgress, key:'progress' },
            { label:'Sync Data',         icon:'cloud-sync',      color:'#3B82F6', action: () => { setIsRefreshing(true); loadData(null,null,true); }, key:'sync' },
            { label:'View Pipeline',     icon:'pipe',            color:'#F59E0B', action: () => navigation.navigate('AutomatedHiringPipelineScreen'), key:'pipe' },
            { label:'Add Rule',          icon:'cog-plus',        color:'#EF4444', action: () => { setEditRule(null); setRuleForm({name:'',trigger:'',condition:'',action:'',threshold:'75',active:true}); setRuleModal(true); }, key:'rule' },
            { label:'Export Report',     icon:'export-variant',  color:'#06B6D4', action: () => setExportModal(true), key:'export' },
          ].map(qa => (
            <TouchableOpacity key={qa.key} style={s.quickCard} onPress={qa.action} disabled={processing===qa.key}>
              <View style={[s.quickIcon, { backgroundColor: qa.color+'22' }]}>
                {processing===qa.key
                  ? <ActivityIndicator size={20} color={qa.color} />
                  : <MaterialCommunityIcons name={qa.icon} size={22} color={qa.color} />
                }
              </View>
              <Text style={s.quickLabel}>{qa.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  // ─── TAB: Pipeline ────────────────────────────────────────────────────────
  const renderPipeline = () => (
    <ScrollView contentContainerStyle={{ padding:16, paddingBottom:100 }}
      refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={() => { setIsRefreshing(true); loadData(null,null,true); }} />}
    >
      {/* Stage-by-stage table */}
      <View style={s.card}>
        <Text style={s.cardTitle}>Stage Breakdown</Text>
        <View style={s.tableHeader}>
          <Text style={[s.tableCell, { flex:2 }]}>Stage</Text>
          <Text style={s.tableCell}>Count</Text>
          <Text style={s.tableCell}>Share</Text>
          <Text style={s.tableCell}>Conv.</Text>
        </View>
        {STAGES.slice(1).map((st, idx) => {
          const cnt      = M.stageMap[st.id] || 0;
          const prev     = idx > 0 ? (M.stageMap[STAGES[idx].id]||M.total||1) : (M.total||1);
          const share    = pct(cnt, M.total||1);
          const conv     = pct(cnt, prev||1);
          return (
            <View key={st.id} style={[s.tableRow, idx%2===1 && { backgroundColor:'#F9FAFB' }]}>
              <View style={[s.tableCell, { flex:2, flexDirection:'row', alignItems:'center', gap:8 }]}>
                <View style={[s.stageDot, { backgroundColor: st.color }]} />
                <Text style={s.tableCellText} numberOfLines={1}>{st.title}</Text>
              </View>
              <Text style={[s.tableCell, s.tableCellBold]}>{cnt}</Text>
              <Text style={s.tableCell}>{share}%</Text>
              <Text style={[s.tableCell, { color: conv>50?'#10B981':conv>25?'#F59E0B':'#EF4444' }]}>{conv}%</Text>
            </View>
          );
        })}
      </View>

      {/* Conversion funnel visual */}
      <View style={s.card}>
        <Text style={s.cardTitle}>Visual Conversion Funnel</Text>
        {STAGES.slice(1).map((st, idx) => {
          const cnt  = M.stageMap[st.id] || 0;
          const maxW = M.total || 1;
          const barPct = pct(cnt, maxW);
          const leftMargin = (100 - barPct) / 2;
          return (
            <View key={st.id} style={s.vFunnelRow}>
              <Text style={s.vFunnelLabel}>{st.short||st.title.slice(0,8)}</Text>
              <View style={s.vFunnelBg}>
                <View style={[s.vFunnelFill, { width:`${barPct}%`, marginLeft:`${leftMargin}%`, backgroundColor: st.color }]} />
              </View>
              <Text style={s.vFunnelCnt}>{cnt}</Text>
            </View>
          );
        })}
      </View>

      {/* Bottleneck detection */}
      <View style={s.card}>
        <Text style={s.cardTitle}>🚨 Bottleneck Detection</Text>
        {(() => {
          const bottlenecks = STAGES.slice(1)
            .map((st, idx) => {
              const cnt   = M.stageMap[st.id] || 0;
              const prev  = STAGES[idx] ? M.stageMap[STAGES[idx].id]||0 : M.total;
              const conv  = prev ? pct(cnt, prev) : 100;
              return { ...st, cnt, conv };
            })
            .filter(st => st.conv < 30 && st.cnt === 0 && M.stageMap[STAGES[STAGES.findIndex(s=>s.id===st.id)-1]?.id]||0 > 0)
            .slice(0,3);

          // Find stages with most candidates (potential queues)
          const queued = STAGES.slice(1)
            .map(st => ({ ...st, cnt: M.stageMap[st.id]||0 }))
            .sort((a,b) => b.cnt - a.cnt)
            .filter(st => st.cnt > 0)
            .slice(0, 3);

          return queued.length > 0 ? queued.map(st => (
            <View key={st.id} style={s.bottleneckRow}>
              <View style={[s.bottleneckIcon, { backgroundColor: st.color+'22' }]}>
                <MaterialCommunityIcons name={st.icon} size={16} color={st.color} />
              </View>
              <View style={{ flex:1 }}>
                <Text style={s.bottleneckTitle}>{st.title}</Text>
                <Text style={s.bottleneckSub}>{st.cnt} candidates waiting · action required</Text>
              </View>
              <TouchableOpacity style={[s.smallBtn, { borderColor: st.color }]} onPress={() => navigation.navigate('AutomatedHiringPipelineScreen')}>
                <Text style={[s.smallBtnText, { color: st.color }]}>Review</Text>
              </TouchableOpacity>
            </View>
          )) : <Text style={s.emptyText}>No bottlenecks detected 🎉</Text>;
        })()}
      </View>

      {/* Active jobs breakdown */}
      <View style={s.card}>
        <Text style={s.cardTitle}>Active Jobs ({jobs.length})</Text>
        {jobs.slice(0,8).map(j => (
          <View key={j.id} style={s.jobRow}>
            <View style={s.jobDot} />
            <View style={{ flex:1 }}>
              <Text style={s.jobTitle} numberOfLines={1}>{j.title}</Text>
              <Text style={s.jobMeta}>{j.department||'—'} · {j.location||'—'}</Text>
            </View>
            <Text style={s.jobApps}>{j.applicants||0}</Text>
          </View>
        ))}
        {jobs.length === 0 && <Text style={s.emptyText}>No active jobs</Text>}
      </View>
    </ScrollView>
  );

  // ─── TAB: AI Performance ──────────────────────────────────────────────────
  const renderAI = () => (
    <ScrollView contentContainerStyle={{ padding:16, paddingBottom:100 }}>
      {/* Overall AI summary */}
      <LinearGradient colors={['#0e4e96','#8B5CF6']} style={s.aiHero}>
        <MaterialCommunityIcons name="robot" size={48} color="#FFF" />
        <Text style={s.aiHeroTitle}>AI Screening Engine</Text>
        <Text style={s.aiHeroSub}>
          {M.total} candidates processed · {M.avgScore !== null ? `${M.avgScore}% avg score` : 'no scores yet'}
        </Text>
        <View style={s.aiHeroStats}>
          {[
            { label:'Shortlisted', val:M.shortlisted },
            { label:'Qualified',   val:M.qualified   },
            { label:'Hired',       val:M.hired        },
          ].map(k => (
            <View key={k.label} style={s.aiHeroStat}>
              <Text style={s.aiHeroStatVal}>{k.val}</Text>
              <Text style={s.aiHeroStatLabel}>{k.label}</Text>
            </View>
          ))}
        </View>
      </LinearGradient>

      {/* AI Agent cards */}
      <Text style={[s.cardTitle, { marginBottom:12 }]}>AI Agents ({AI_AGENTS.length})</Text>
      {AI_AGENTS.map(agent => {
        const st     = agentStats[agent.id] || {};
        const enabled = st.enabled !== false;
        return (
          <TouchableOpacity key={agent.id} style={s.agentCard} onPress={() => { setSelAgent(agent); setAgentModal(true); }}>
            <View style={[s.agentIcon, { backgroundColor: agent.color+'22' }]}>
              <MaterialCommunityIcons name={agent.icon} size={24} color={agent.color} />
            </View>
            <View style={{ flex:1, marginLeft:12 }}>
              <View style={{ flexDirection:'row', alignItems:'center' }}>
                <Text style={s.agentName}>{agent.name}</Text>
                <View style={[s.statusDot, { backgroundColor: enabled?'#10B981':'#D1D5DB' }]} />
              </View>
              <Text style={s.agentDesc} numberOfLines={1}>{agent.desc}</Text>
              {st.processed > 0 && (
                <Text style={s.agentMeta}>{st.processed} processed{st.avgScore !== null ? ` · avg ${st.avgScore}%` : ''}</Text>
              )}
            </View>
            <Switch
              value={enabled}
              onValueChange={() => toggleAgentEnabled(agent.id)}
              trackColor={{ false:'#D1D5DB', true:'#BFDBFE' }}
              thumbColor={enabled ? agent.color : '#9CA3AF'}
            />
          </TouchableOpacity>
        );
      })}

      {/* Run AI */}
      <TouchableOpacity style={[s.primaryBtn, { marginTop:20 }]} onPress={runAI} disabled={processing==='ai'}>
        <LinearGradient colors={['#8B5CF6','#6D28D9']} style={s.primaryBtnGrad}>
          {processing==='ai'
            ? <ActivityIndicator size={20} color="#FFF" />
            : <MaterialCommunityIcons name="robot" size={20} color="#FFF" />
          }
          <Text style={s.primaryBtnText}>Run AI Scoring Now</Text>
        </LinearGradient>
      </TouchableOpacity>
    </ScrollView>
  );

  // ─── TAB: Workflows ───────────────────────────────────────────────────────
  const renderWorkflows = () => (
    <View style={{ flex:1 }}>
      <View style={s.workflowHeader}>
        <Text style={s.cardTitle}>{rules.length} Automation Rules</Text>
        <TouchableOpacity style={s.addBtn} onPress={() => { setEditRule(null); setRuleForm({name:'',trigger:'',condition:'',action:'',threshold:'75',active:true}); setRuleModal(true); }}>
          <MaterialCommunityIcons name="plus" size={18} color="#FFF" />
          <Text style={s.addBtnText}>Add Rule</Text>
        </TouchableOpacity>
      </View>

      {/* Stats */}
      <View style={[s.card, { marginHorizontal:16, marginBottom:12 }]}>
        <View style={s.ruleStatsRow}>
          {[
            { label:'Total Rules',   val:rules.length,    color:'#3B82F6' },
            { label:'Active',        val:M.activeRules,   color:'#10B981' },
            { label:'Times Fired',   val:M.rulesRan,      color:'#F59E0B' },
          ].map(r => (
            <View key={r.label} style={s.ruleStat}>
              <Text style={[s.ruleStatVal, { color:r.color }]}>{r.val}</Text>
              <Text style={s.ruleStatLabel}>{r.label}</Text>
            </View>
          ))}
        </View>
      </View>

      <FlatList
        data={rules}
        keyExtractor={item => String(item.id)}
        contentContainerStyle={{ paddingHorizontal:16, paddingBottom:100 }}
        renderItem={({ item }) => (
          <View style={s.ruleCard}>
            <View style={{ flexDirection:'row', alignItems:'flex-start' }}>
              <View style={[s.ruleIcon, { backgroundColor: item.active||item.enabled ? '#10B98122':'#F3F4F6' }]}>
                <MaterialCommunityIcons name="cog-play" size={20} color={item.active||item.enabled?'#10B981':'#9CA3AF'} />
              </View>
              <View style={{ flex:1, marginLeft:12 }}>
                <Text style={s.ruleName}>{item.name}</Text>
                <Text style={s.ruleMeta}>🔔 {item.trigger||'Trigger not set'}</Text>
                {item.condition && <Text style={s.ruleMeta}>⚙️ {item.condition}</Text>}
                <Text style={s.ruleMeta}>▶️ {item.action||'Action not set'}</Text>
              </View>
              <Switch
                value={item.active||item.enabled||false}
                onValueChange={() => toggleRule(item)}
                trackColor={{ false:'#D1D5DB', true:'#BFDBFE' }}
                thumbColor={item.active||item.enabled ? '#10B981':'#9CA3AF'}
              />
            </View>
            <View style={s.ruleFooter}>
              <Text style={s.ruleFireCount}>Fired {item.execution_count||item.times_fired||0} times</Text>
              <TouchableOpacity style={s.ruleEditBtn} onPress={() => { setEditRule(item); setRuleForm({ name:item.name||'', trigger:item.trigger||'', condition:item.condition||'', action:item.action||'', threshold:String(item.threshold||75), active:item.active||item.enabled||true }); setRuleModal(true); }}>
                <MaterialCommunityIcons name="pencil" size={16} color="#3B82F6" />
                <Text style={[s.ruleEditBtnText, { color:'#3B82F6' }]}>Edit</Text>
              </TouchableOpacity>
              <TouchableOpacity style={s.ruleEditBtn} onPress={() => deleteRule(item)}>
                <MaterialCommunityIcons name="delete" size={16} color="#EF4444" />
                <Text style={[s.ruleEditBtnText, { color:'#EF4444' }]}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
        ListEmptyComponent={
          <View style={s.emptyCard}>
            <MaterialCommunityIcons name="cog-outline" size={52} color="#D1D5DB" />
            <Text style={s.emptyCardTitle}>No Automation Rules</Text>
            <Text style={s.emptyCardSub}>Create rules to automate your hiring workflow</Text>
            <TouchableOpacity style={[s.addBtn, { marginTop:16 }]} onPress={() => { setEditRule(null); setRuleForm({name:'',trigger:'',condition:'',action:'',threshold:'75',active:true}); setRuleModal(true); }}>
              <MaterialCommunityIcons name="plus" size={18} color="#FFF" />
              <Text style={s.addBtnText}>Create First Rule</Text>
            </TouchableOpacity>
          </View>
        }
      />
    </View>
  );

  // ─── TAB: Activity ────────────────────────────────────────────────────────
  const renderActivity = () => (
    <FlatList
      data={activity}
      keyExtractor={(item, i) => String(item.id||i)}
      contentContainerStyle={{ padding:16, paddingBottom:100 }}
      refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={() => { setIsRefreshing(true); loadData(null,null,true); }} />}
      renderItem={({ item, index }) => renderActivityItem(item, index)}
      ListHeaderComponent={
        <View style={[s.card, { marginBottom:12 }]}>
          <View style={s.ruleStatsRow}>
            {[
              { label:'Events Today',  val: activity.filter(a=>new Date(a.created_at||a.timestamp).toDateString()===new Date().toDateString()).length, color:'#3B82F6' },
              { label:'This Week',     val: activity.filter(a=>(Date.now()-new Date(a.created_at||a.timestamp).getTime())<604800000).length, color:'#10B981' },
              { label:'Total Logged',  val: activity.length, color:'#8B5CF6' },
            ].map(r => (
              <View key={r.label} style={s.ruleStat}>
                <Text style={[s.ruleStatVal, { color:r.color }]}>{r.val}</Text>
                <Text style={s.ruleStatLabel}>{r.label}</Text>
              </View>
            ))}
          </View>
        </View>
      }
      ListEmptyComponent={
        <View style={s.emptyCard}>
          <MaterialCommunityIcons name="timeline-text-outline" size={52} color="#D1D5DB" />
          <Text style={s.emptyCardTitle}>No Activity Yet</Text>
          <Text style={s.emptyCardSub}>Activity will appear here as candidates move through the pipeline</Text>
        </View>
      }
    />
  );

  const renderActivityItem = (act, i) => {
    const type   = act.event_type || act.type || 'applied';
    const ai     = ACTIVITY_ICONS[type] || { icon:'circle-small', color:'#9CA3AF' };
    return (
      <View key={i} style={s.actRow}>
        <View style={[s.actIcon, { backgroundColor: ai.color+'22' }]}>
          <MaterialCommunityIcons name={ai.icon} size={16} color={ai.color} />
        </View>
        <View style={{ flex:1, marginLeft:10 }}>
          <Text style={s.actTitle} numberOfLines={1}>{act.description || act.message || `${type} event`}</Text>
          <Text style={s.actMeta}>{act.candidate_name||act.actor||'System'} · {fmtAgo(act.created_at||act.timestamp)}</Text>
        </View>
        {act.score !== undefined && act.score !== null && (
          <Text style={[s.actScore, { color: sc(act.score) }]}>{act.score}%</Text>
        )}
      </View>
    );
  };

  // ─── TAB: Settings ────────────────────────────────────────────────────────
  const renderSettings = () => (
    <ScrollView contentContainerStyle={{ padding:16, paddingBottom:100 }}>
      {/* Auto progression */}
      <View style={s.card}>
        <Text style={s.cardTitle}>Auto-Progression</Text>
        <View style={s.settingRow}>
          <View style={{ flex:1 }}>
            <Text style={s.settingLabel}>Enable Auto-Progression</Text>
            <Text style={s.settingSub}>Automatically move candidates above score threshold</Text>
          </View>
          <Switch value={settings.autoProgress} onValueChange={v => setSettings(s=>({...s,autoProgress:v}))} trackColor={{ false:'#D1D5DB', true:'#BFDBFE' }} thumbColor={settings.autoProgress?'#0e4e96':'#9CA3AF'} />
        </View>
        <Text style={s.settingLabel}>Auto-Progression Score Threshold: {settings.autoProgressScore}%</Text>
        <View style={s.sliderRow}>
          {[50,60,65,70,75,80,85,90].map(v => (
            <TouchableOpacity key={v} style={[s.sliderBtn, settings.autoProgressScore===v && s.sliderBtnActive]} onPress={() => setSettings(s=>({...s,autoProgressScore:v}))}>
              <Text style={[s.sliderBtnText, settings.autoProgressScore===v && s.sliderBtnTextActive]}>{v}%</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* AI thresholds */}
      <View style={s.card}>
        <Text style={s.cardTitle}>AI Thresholds</Text>
        <View style={s.settingRow}>
          <View style={{ flex:1 }}>
            <Text style={s.settingLabel}>Auto-Score New Applications</Text>
            <Text style={s.settingSub}>Run AI as soon as application is received</Text>
          </View>
          <Switch value={settings.aiAutoScore} onValueChange={v => setSettings(s=>({...s,aiAutoScore:v}))} trackColor={{ false:'#D1D5DB', true:'#BFDBFE' }} thumbColor={settings.aiAutoScore?'#0e4e96':'#9CA3AF'} />
        </View>
        <Text style={s.settingLabel}>Shortlist Threshold: {settings.shortlistThreshold}%</Text>
        <View style={s.sliderRow}>
          {[55,60,65,70,75,80,85].map(v => (
            <TouchableOpacity key={v} style={[s.sliderBtn, settings.shortlistThreshold===v && s.sliderBtnActive]} onPress={() => setSettings(s=>({...s,shortlistThreshold:v}))}>
              <Text style={[s.sliderBtnText, settings.shortlistThreshold===v && s.sliderBtnTextActive]}>{v}%</Text>
            </TouchableOpacity>
          ))}
        </View>
        <Text style={[s.settingLabel, { marginTop:12 }]}>Auto-Reject Threshold: {settings.rejectThreshold}%</Text>
        <View style={s.sliderRow}>
          {[20,25,30,35,40,45,50].map(v => (
            <TouchableOpacity key={v} style={[s.sliderBtn, settings.rejectThreshold===v && { backgroundColor:'#FEF2F2', borderColor:'#EF4444' }]} onPress={() => setSettings(s=>({...s,rejectThreshold:v}))}>
              <Text style={[s.sliderBtnText, settings.rejectThreshold===v && { color:'#EF4444' }]}>{v}%</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Notifications */}
      <View style={s.card}>
        <Text style={s.cardTitle}>Notifications</Text>
        {[
          { key:'emailCandidates', label:'Email Candidates on Stage Move', sub:'Send automated emails when candidates advance' },
          { key:'emailRecruiter',  label:'Email Recruiter Alerts',          sub:'Notify recruiters of key pipeline events'    },
          { key:'slackNotify',     label:'Slack Notifications',             sub:'Post to Slack channel on key events'         },
        ].map(n => (
          <View key={n.key} style={s.settingRow}>
            <View style={{ flex:1 }}>
              <Text style={s.settingLabel}>{n.label}</Text>
              <Text style={s.settingSub}>{n.sub}</Text>
            </View>
            <Switch value={settings[n.key]} onValueChange={v => setSettings(s=>({...s,[n.key]:v}))} trackColor={{ false:'#D1D5DB', true:'#BFDBFE' }} thumbColor={settings[n.key]?'#0e4e96':'#9CA3AF'} />
          </View>
        ))}
      </View>

      {/* Sync settings */}
      <View style={s.card}>
        <Text style={s.cardTitle}>Sync & Retention</Text>
        <Text style={s.settingLabel}>Auto-Sync Interval: every {settings.syncInterval} min</Text>
        <View style={s.sliderRow}>
          {[1,2,5,10,15,30].map(v => (
            <TouchableOpacity key={v} style={[s.sliderBtn, settings.syncInterval===v && s.sliderBtnActive]} onPress={() => setSettings(s=>({...s,syncInterval:v}))}>
              <Text style={[s.sliderBtnText, settings.syncInterval===v && s.sliderBtnTextActive]}>{v}m</Text>
            </TouchableOpacity>
          ))}
        </View>
        <Text style={[s.settingLabel, { marginTop:12 }]}>Activity Log Retention: {settings.retentionDays} days</Text>
        <View style={s.sliderRow}>
          {[30,60,90,180,365].map(v => (
            <TouchableOpacity key={v} style={[s.sliderBtn, settings.retentionDays===v && s.sliderBtnActive]} onPress={() => setSettings(s=>({...s,retentionDays:v}))}>
              <Text style={[s.sliderBtnText, settings.retentionDays===v && s.sliderBtnTextActive]}>{v}d</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Sync status */}
      <View style={s.card}>
        <Text style={s.cardTitle}>Sync Status</Text>
        <View style={s.syncStatusRow}>
          <MaterialCommunityIcons name="cloud-check" size={20} color="#10B981" />
          <Text style={s.syncStatusText}>Last sync: {lastSync ? fmtTime(lastSync) : 'Not synced'}</Text>
        </View>
        <View style={s.syncStatusRow}>
          <MaterialCommunityIcons name="database" size={20} color="#3B82F6" />
          <Text style={s.syncStatusText}>{applications.length} applications · {jobs.length} jobs · {rules.length} rules</Text>
        </View>
      </View>

      <TouchableOpacity style={s.primaryBtn} onPress={saveSettings} disabled={processing==='settings'}>
        <LinearGradient colors={['#0e4e96','#1e7be2']} style={s.primaryBtnGrad}>
          {processing==='settings' ? <ActivityIndicator size={18} color="#FFF" /> : <MaterialCommunityIcons name="content-save" size={18} color="#FFF" />}
          <Text style={s.primaryBtnText}>Save Settings</Text>
        </LinearGradient>
      </TouchableOpacity>
    </ScrollView>
  );

  // ─── Modal: Rule editor ───────────────────────────────────────────────────
  const renderRuleModal = () => (
    <Modal visible={ruleModal} animationType="slide" transparent onRequestClose={() => setRuleModal(false)}>
      <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={s.modalOverlay}>
        <View style={s.formSheet}>
          <Text style={s.formTitle}>{editRule ? 'Edit Rule' : '+ New Automation Rule'}</Text>

          <Text style={s.fieldLabel}>Rule Name *</Text>
          <TextInput style={s.field} placeholder="e.g. Auto-shortlist top scorers" placeholderTextColor="#BBB"
            value={ruleForm.name} onChangeText={v => setRuleForm(f=>({...f,name:v}))} />

          <Text style={s.fieldLabel}>Trigger *</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:14 }}>
            {RULE_TRIGGERS.map(t => (
              <TouchableOpacity key={t} style={[s.optBtn, ruleForm.trigger===t && s.optBtnActive]} onPress={() => setRuleForm(f=>({...f,trigger:t}))}>
                <Text style={[s.optBtnText, ruleForm.trigger===t && s.optBtnTextActive]}>{t}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <Text style={s.fieldLabel}>Condition</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:14 }}>
            {RULE_CONDITIONS.map(c => (
              <TouchableOpacity key={c} style={[s.optBtn, ruleForm.condition===c && s.optBtnActive]} onPress={() => setRuleForm(f=>({...f,condition:c}))}>
                <Text style={[s.optBtnText, ruleForm.condition===c && s.optBtnTextActive]}>{c}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          {ruleForm.condition?.includes('threshold') && (
            <>
              <Text style={s.fieldLabel}>Score Threshold: {ruleForm.threshold}%</Text>
              <View style={s.sliderRow}>
                {[40,50,60,65,70,75,80,85,90].map(v => (
                  <TouchableOpacity key={v} style={[s.sliderBtn, parseInt(ruleForm.threshold)===v && s.sliderBtnActive]} onPress={() => setRuleForm(f=>({...f,threshold:String(v)}))}>
                    <Text style={[s.sliderBtnText, parseInt(ruleForm.threshold)===v && s.sliderBtnTextActive]}>{v}%</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </>
          )}

          <Text style={s.fieldLabel}>Action *</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:14 }}>
            {RULE_ACTIONS.map(a => (
              <TouchableOpacity key={a} style={[s.optBtn, ruleForm.action===a && s.optBtnActive]} onPress={() => setRuleForm(f=>({...f,action:a}))}>
                <Text style={[s.optBtnText, ruleForm.action===a && s.optBtnTextActive]}>{a}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <View style={s.settingRow}>
            <Text style={s.settingLabel}>Rule Active</Text>
            <Switch value={ruleForm.active} onValueChange={v => setRuleForm(f=>({...f,active:v}))} trackColor={{ false:'#D1D5DB', true:'#BFDBFE' }} thumbColor={ruleForm.active?'#0e4e96':'#9CA3AF'} />
          </View>

          <View style={s.formBtns}>
            <TouchableOpacity style={s.formBtnSecondary} onPress={() => setRuleModal(false)}>
              <Text style={s.formBtnSecondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.formBtnPrimary} onPress={saveRule} disabled={processing==='rule'}>
              {processing==='rule' ? <ActivityIndicator size={16} color="#FFF" /> : <Text style={s.formBtnPrimaryText}>{editRule ? 'Update Rule' : 'Create Rule'}</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  // ─── Modal: Agent detail ──────────────────────────────────────────────────
  const renderAgentModal = () => {
    const agent = selAgent;
    if (!agent) return null;
    const st      = agentStats[agent.id] || {};
    const enabled = st.enabled !== false;
    return (
      <Modal visible={agentModal} animationType="slide" transparent onRequestClose={() => setAgentModal(false)}>
        <View style={s.modalOverlay}>
          <View style={s.formSheet}>
            <LinearGradient colors={[agent.color, agent.color+'88']} style={s.agentHero}>
              <TouchableOpacity style={s.closeAbsolute} onPress={() => setAgentModal(false)}>
                <MaterialCommunityIcons name="close" size={20} color="#FFF" />
              </TouchableOpacity>
              <MaterialCommunityIcons name={agent.icon} size={40} color="#FFF" />
              <Text style={s.agentHeroName}>{agent.name}</Text>
              <Text style={s.agentHeroDesc}>{agent.desc}</Text>
            </LinearGradient>
            <View style={{ padding:20 }}>
              <View style={s.agentStatGrid}>
                {[
                  { label:'Processed',  val: fmtN(st.processed||0) },
                  { label:'Avg Score',  val: st.avgScore !== null && st.avgScore !== undefined ? `${st.avgScore}%` : '—' },
                  { label:'Status',     val: enabled ? 'Active' : 'Disabled' },
                ].map(k => (
                  <View key={k.label} style={[s.agentStatCard, { borderTopColor: agent.color }]}>
                    <Text style={s.agentStatVal}>{k.val}</Text>
                    <Text style={s.agentStatLabel}>{k.label}</Text>
                  </View>
                ))}
              </View>
              <View style={[s.settingRow, { marginTop:20 }]}>
                <Text style={s.settingLabel}>Agent Enabled</Text>
                <Switch value={enabled} onValueChange={() => toggleAgentEnabled(agent.id)} trackColor={{ false:'#D1D5DB', true:'#BFDBFE' }} thumbColor={enabled ? agent.color : '#9CA3AF'} />
              </View>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // ─── Modal: Export ────────────────────────────────────────────────────────
  const renderExportModal = () => (
    <Modal visible={exportModal} animationType="fade" transparent onRequestClose={() => setExportModal(false)}>
      <TouchableOpacity style={s.modalOverlay} activeOpacity={1} onPress={() => setExportModal(false)}>
        <View style={[s.formSheet, { maxHeight:380 }]}>
          <Text style={s.formTitle}>Export Report</Text>
          {[
            { label:'Pipeline Summary (PDF)',      icon:'file-pdf-box',   color:'#EF4444' },
            { label:'Candidates CSV',              icon:'file-delimited', color:'#10B981' },
            { label:'AI Score Report',             icon:'brain',          color:'#8B5CF6' },
            { label:'Automation Activity Log',     icon:'cog-transfer',   color:'#F59E0B' },
            { label:'Full Analytics Report',       icon:'chart-bar',      color:'#3B82F6' },
          ].map(e => (
            <TouchableOpacity key={e.label} style={s.exportRow} onPress={() => { setExportModal(false); Alert.alert('Export', `${e.label} export queued — check your email shortly.`); }}>
              <MaterialCommunityIcons name={e.icon} size={22} color={e.color} />
              <Text style={s.exportLabel}>{e.label}</Text>
              <MaterialCommunityIcons name="chevron-right" size={18} color="#D1D5DB" />
            </TouchableOpacity>
          ))}
        </View>
      </TouchableOpacity>
    </Modal>
  );

  // ─── Loading ──────────────────────────────────────────────────────────────
  if (isLoading) return (
    <View style={s.loadingBox}>
      <LinearGradient colors={['#0e4e96','#1e7be2']} style={{ padding:32, borderRadius:20, alignItems:'center' }}>
        <MaterialCommunityIcons name="chart-line" size={44} color="#FFF" />
        <ActivityIndicator size={36} color="#FFF" style={{ marginTop:16 }} />
        <Text style={{ color:'#FFF', fontSize:18, fontWeight:'bold', marginTop:12 }}>Automation Dashboard</Text>
        <Text style={{ color:'rgba(255,255,255,0.7)', fontSize:14, marginTop:6 }}>Loading analytics…</Text>
      </LinearGradient>
    </View>
  );

  // ─── Root ─────────────────────────────────────────────────────────────────
  return (
    <View style={s.root}>
      {renderHeader()}
      {renderTabBar()}

      <View style={{ flex:1 }}>
        {tab === 'overview'  && renderOverview()}
        {tab === 'pipeline'  && renderPipeline()}
        {tab === 'ai'        && renderAI()}
        {tab === 'workflows' && renderWorkflows()}
        {tab === 'activity'  && renderActivity()}
        {tab === 'settings'  && renderSettings()}
      </View>

      {renderRuleModal()}
      {renderAgentModal()}
      {renderExportModal()}
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  root:         { flex:1, backgroundColor:'#F3F4F6' },
  loadingBox:   { flex:1, justifyContent:'center', alignItems:'center', backgroundColor:'#F3F4F6' },
  // Header
  header:       { paddingTop: Platform.OS==='ios'?52:32, paddingBottom:14, paddingHorizontal:16 },
  headerRow:    { flexDirection:'row', alignItems:'center' },
  headerTitle:  { fontSize:20, fontWeight:'bold', color:'#FFF' },
  headerSub:    { fontSize:12, color:'rgba(255,255,255,0.7)', marginTop:2 },
  iconBtn:      { width:38, height:38, borderRadius:19, backgroundColor:'rgba(255,255,255,0.18)', justifyContent:'center', alignItems:'center' },
  periodBtn:    { borderRadius:20, paddingHorizontal:14, paddingVertical:6, marginRight:8, backgroundColor:'rgba(255,255,255,0.15)' },
  periodBtnActive:{ backgroundColor:'#FFF' },
  periodText:   { fontSize:13, color:'rgba(255,255,255,0.85)', fontWeight:'500' },
  periodTextActive:{ color:'#0e4e96', fontWeight:'700' },
  // Tab bar
  tabBar:       { backgroundColor:'#FFF', borderBottomWidth:1, borderBottomColor:'#E5E7EB', maxHeight:50, flexGrow:0 },
  tab:          { flexDirection:'row', alignItems:'center', paddingHorizontal:14, paddingVertical:12, gap:5, borderBottomWidth:2, borderBottomColor:'transparent' },
  tabActive:    { borderBottomColor:'#0e4e96' },
  tabText:      { fontSize:13, color:'#9CA3AF', fontWeight:'500' },
  tabTextActive:{ color:'#0e4e96', fontWeight:'700' },
  // Cards
  card:         { backgroundColor:'#FFF', borderRadius:14, padding:16, marginBottom:12, elevation:1, shadowColor:'#000', shadowOpacity:0.04, shadowRadius:4, shadowOffset:{width:0,height:2} },
  cardTitle:    { fontSize:16, fontWeight:'700', color:'#111', marginBottom:12 },
  cardTitleRow: { flexDirection:'row', alignItems:'center', justifyContent:'space-between', marginBottom:12 },
  viewAll:      { fontSize:13, color:'#0e4e96', fontWeight:'600' },
  // KPI grid
  kpiRow:       { flexDirection:'row', gap:10, marginBottom:10 },
  kpiCard:      { flex:1, backgroundColor:'#FFF', borderRadius:12, padding:12, alignItems:'center', borderTopWidth:3, elevation:1, shadowColor:'#000', shadowOpacity:0.04, shadowRadius:3, shadowOffset:{width:0,height:1} },
  kpiVal:       { fontSize:20, fontWeight:'bold', color:'#111', marginTop:6 },
  kpiLabel:     { fontSize:10, color:'#6B7280', marginTop:2, textAlign:'center' },
  // Metric row
  metricRow:    { flexDirection:'row', alignItems:'center', marginBottom:10, gap:8 },
  metricLabel:  { fontSize:13, color:'#6B7280', width:130 },
  metricBarBg:  { flex:1, height:8, backgroundColor:'#F3F4F6', borderRadius:4, overflow:'hidden' },
  metricBarFill:{ height:'100%', borderRadius:4 },
  metricVal:    { fontSize:13, fontWeight:'700', width:38, textAlign:'right' },
  // Funnel
  funnelRow:    { flexDirection:'row', alignItems:'center', marginBottom:8, gap:6 },
  funnelLabel:  { fontSize:11, color:'#6B7280', flex:1 },
  funnelBarBg:  { flex:2, height:12, backgroundColor:'#F3F4F6', borderRadius:6, overflow:'hidden' },
  funnelBarFill:{ height:'100%', borderRadius:6 },
  funnelCount:  { fontSize:12, fontWeight:'700', color:'#374151', width:24, textAlign:'right' },
  // Score distribution
  distribRow:   { flexDirection:'row', justifyContent:'space-around', alignItems:'flex-end', height:110 },
  distribCol:   { alignItems:'center', flex:1 },
  distribBarBg: { width:22, height:90, backgroundColor:'#F3F4F6', borderRadius:4, justifyContent:'flex-end', overflow:'hidden' },
  distribBarFill:{ width:'100%', borderRadius:4 },
  distribNum:   { fontSize:11, fontWeight:'700', color:'#374151', marginTop:4 },
  distribLabel: { fontSize:9, color:'#9CA3AF', marginTop:2 },
  // Visual funnel
  vFunnelRow:   { flexDirection:'row', alignItems:'center', marginBottom:6, gap:6 },
  vFunnelLabel: { fontSize:10, color:'#6B7280', width:56, textAlign:'right' },
  vFunnelBg:    { flex:1, height:16, backgroundColor:'#F3F4F6', borderRadius:8, overflow:'hidden' },
  vFunnelFill:  { height:'100%', borderRadius:8 },
  vFunnelCnt:   { fontSize:12, fontWeight:'700', color:'#374151', width:24 },
  // Table
  tableHeader:  { flexDirection:'row', paddingVertical:8, borderBottomWidth:2, borderBottomColor:'#E5E7EB', marginBottom:4 },
  tableRow:     { flexDirection:'row', paddingVertical:10, borderRadius:6 },
  tableCell:    { flex:1, fontSize:12, color:'#6B7280', fontWeight:'500' },
  tableCellText:{ fontSize:13, color:'#111' },
  tableCellBold:{ fontWeight:'700', color:'#111' },
  stageDot:     { width:8, height:8, borderRadius:4, marginTop:3 },
  // Bottleneck
  bottleneckRow:{ flexDirection:'row', alignItems:'center', backgroundColor:'#FFF9F0', borderRadius:10, padding:10, marginBottom:8, borderWidth:1, borderColor:'#FDE68A' },
  bottleneckIcon:{ width:34, height:34, borderRadius:17, justifyContent:'center', alignItems:'center' },
  bottleneckTitle:{ fontSize:14, fontWeight:'600', color:'#111' },
  bottleneckSub:{ fontSize:12, color:'#9CA3AF', marginTop:1 },
  smallBtn:     { borderRadius:8, borderWidth:1, paddingHorizontal:8, paddingVertical:4 },
  smallBtnText: { fontSize:12, fontWeight:'600' },
  // Jobs
  jobRow:       { flexDirection:'row', alignItems:'center', paddingVertical:8, borderBottomWidth:1, borderBottomColor:'#F3F4F6', gap:10 },
  jobDot:       { width:8, height:8, borderRadius:4, backgroundColor:'#10B981', flexShrink:0 },
  jobTitle:     { fontSize:14, fontWeight:'600', color:'#111' },
  jobMeta:      { fontSize:12, color:'#9CA3AF', marginTop:1 },
  jobApps:      { fontSize:13, fontWeight:'700', color:'#0e4e96' },
  // Quick grid
  quickGrid:    { flexDirection:'row', flexWrap:'wrap', gap:10 },
  quickCard:    { alignItems:'center', width:(width-72)/3, backgroundColor:'#F9FAFB', borderRadius:12, paddingVertical:14 },
  quickIcon:    { width:44, height:44, borderRadius:22, justifyContent:'center', alignItems:'center', marginBottom:6 },
  quickLabel:   { fontSize:10, color:'#374151', textAlign:'center', fontWeight:'500' },
  // AI Hero
  aiHero:       { borderRadius:16, padding:24, alignItems:'center', marginBottom:16 },
  aiHeroTitle:  { color:'#FFF', fontSize:20, fontWeight:'bold', marginTop:10 },
  aiHeroSub:    { color:'rgba(255,255,255,0.8)', fontSize:13, marginTop:4 },
  aiHeroStats:  { flexDirection:'row', gap:32, marginTop:16 },
  aiHeroStat:   { alignItems:'center' },
  aiHeroStatVal:{ color:'#FFF', fontSize:22, fontWeight:'bold' },
  aiHeroStatLabel:{ color:'rgba(255,255,255,0.75)', fontSize:12, marginTop:2 },
  // Agent cards
  agentCard:    { flexDirection:'row', alignItems:'center', backgroundColor:'#FFF', borderRadius:14, padding:14, marginBottom:10, elevation:1, shadowColor:'#000', shadowOpacity:0.04, shadowRadius:3, shadowOffset:{width:0,height:1} },
  agentIcon:    { width:48, height:48, borderRadius:24, justifyContent:'center', alignItems:'center' },
  agentName:    { fontSize:14, fontWeight:'700', color:'#111' },
  agentDesc:    { fontSize:12, color:'#9CA3AF', marginTop:2 },
  agentMeta:    { fontSize:11, color:'#6B7280', marginTop:2 },
  statusDot:    { width:7, height:7, borderRadius:4, marginLeft:6 },
  // Agent hero modal
  agentHero:    { borderTopLeftRadius:24, borderTopRightRadius:24, padding:28, alignItems:'center' },
  agentHeroName:{ color:'#FFF', fontSize:20, fontWeight:'bold', marginTop:10 },
  agentHeroDesc:{ color:'rgba(255,255,255,0.8)', fontSize:13, marginTop:4, textAlign:'center' },
  closeAbsolute:{ position:'absolute', right:16, top:16, width:30, height:30, borderRadius:15, backgroundColor:'rgba(255,255,255,0.2)', justifyContent:'center', alignItems:'center' },
  agentStatGrid:{ flexDirection:'row', gap:10 },
  agentStatCard:{ flex:1, backgroundColor:'#F9FAFB', borderRadius:10, padding:12, alignItems:'center', borderTopWidth:3 },
  agentStatVal: { fontSize:18, fontWeight:'bold', color:'#111' },
  agentStatLabel:{ fontSize:11, color:'#9CA3AF', marginTop:3 },
  // Workflow
  workflowHeader:{ flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:16, paddingVertical:12 },
  addBtn:       { flexDirection:'row', alignItems:'center', backgroundColor:'#0e4e96', borderRadius:10, paddingHorizontal:14, paddingVertical:8, gap:4 },
  addBtnText:   { color:'#FFF', fontSize:14, fontWeight:'600' },
  ruleStatsRow: { flexDirection:'row', justifyContent:'space-around' },
  ruleStat:     { alignItems:'center' },
  ruleStatVal:  { fontSize:22, fontWeight:'bold' },
  ruleStatLabel:{ fontSize:11, color:'#9CA3AF', marginTop:2 },
  ruleCard:     { backgroundColor:'#FFF', borderRadius:14, padding:14, marginBottom:10, elevation:1, shadowColor:'#000', shadowOpacity:0.04, shadowRadius:3, shadowOffset:{width:0,height:1} },
  ruleIcon:     { width:40, height:40, borderRadius:20, justifyContent:'center', alignItems:'center' },
  ruleName:     { fontSize:14, fontWeight:'700', color:'#111', marginBottom:4 },
  ruleMeta:     { fontSize:12, color:'#6B7280', marginBottom:2 },
  ruleFooter:   { flexDirection:'row', alignItems:'center', marginTop:10, paddingTop:10, borderTopWidth:1, borderTopColor:'#F3F4F6', gap:12 },
  ruleFireCount:{ fontSize:11, color:'#9CA3AF', flex:1 },
  ruleEditBtn:  { flexDirection:'row', alignItems:'center', gap:4 },
  ruleEditBtnText:{ fontSize:12, fontWeight:'600' },
  // Empty
  emptyCard:    { alignItems:'center', paddingVertical:60 },
  emptyCardTitle:{ fontSize:18, fontWeight:'bold', color:'#374151', marginTop:12 },
  emptyCardSub: { fontSize:13, color:'#9CA3AF', marginTop:6, textAlign:'center' },
  emptyText:    { fontSize:13, color:'#9CA3AF', textAlign:'center', paddingVertical:12 },
  // Activity
  actRow:       { flexDirection:'row', alignItems:'center', backgroundColor:'#FFF', borderRadius:10, padding:12, marginBottom:8 },
  actIcon:      { width:34, height:34, borderRadius:17, justifyContent:'center', alignItems:'center' },
  actTitle:     { fontSize:14, fontWeight:'600', color:'#111' },
  actMeta:      { fontSize:11, color:'#9CA3AF', marginTop:2 },
  actScore:     { fontSize:13, fontWeight:'bold', marginLeft:8 },
  // Settings
  settingRow:   { flexDirection:'row', alignItems:'center', marginBottom:16 },
  settingLabel: { fontSize:14, fontWeight:'600', color:'#374151', marginBottom:4 },
  settingSub:   { fontSize:12, color:'#9CA3AF' },
  sliderRow:    { flexDirection:'row', flexWrap:'wrap', gap:8, marginBottom:6 },
  sliderBtn:    { borderRadius:20, borderWidth:1, borderColor:'#D1D5DB', paddingHorizontal:12, paddingVertical:6, backgroundColor:'#F9FAFB' },
  sliderBtnActive:{ backgroundColor:'#0e4e96', borderColor:'#0e4e96' },
  sliderBtnText:{ fontSize:13, color:'#374151' },
  sliderBtnTextActive:{ color:'#FFF' },
  syncStatusRow:{ flexDirection:'row', alignItems:'center', gap:10, paddingVertical:8, borderBottomWidth:1, borderBottomColor:'#F3F4F6' },
  syncStatusText:{ fontSize:13, color:'#374151' },
  // Export
  exportRow:    { flexDirection:'row', alignItems:'center', paddingVertical:14, borderBottomWidth:1, borderBottomColor:'#F3F4F6', gap:14 },
  exportLabel:  { fontSize:15, color:'#374151', flex:1 },
  // Primary button
  primaryBtn:   { borderRadius:14, overflow:'hidden', elevation:2, shadowColor:'#0e4e96', shadowOpacity:0.2, shadowRadius:4, shadowOffset:{width:0,height:2} },
  primaryBtnGrad:{ flexDirection:'row', alignItems:'center', justifyContent:'center', padding:16, gap:10 },
  primaryBtnText:{ color:'#FFF', fontSize:16, fontWeight:'bold' },
  // Modal
  modalOverlay: { flex:1, backgroundColor:'rgba(0,0,0,0.55)', justifyContent:'flex-end' },
  formSheet:    { backgroundColor:'#FFF', borderTopLeftRadius:24, borderTopRightRadius:24, padding:20, paddingBottom: Platform.OS==='ios'?34:20, maxHeight: height*0.88 },
  formTitle:    { fontSize:20, fontWeight:'bold', color:'#111', marginBottom:16 },
  fieldLabel:   { fontSize:13, fontWeight:'600', color:'#374151', marginBottom:6 },
  field:        { backgroundColor:'#F9FAFB', borderWidth:1, borderColor:'#E5E7EB', borderRadius:10, padding:12, fontSize:14, color:'#111', marginBottom:14 },
  formBtns:     { flexDirection:'row', gap:12, marginTop:8 },
  formBtnPrimary:{ flex:1, backgroundColor:'#0e4e96', borderRadius:12, padding:14, alignItems:'center' },
  formBtnPrimaryText:{ color:'#FFF', fontWeight:'bold', fontSize:15 },
  formBtnSecondary:{ flex:1, backgroundColor:'#F3F4F6', borderRadius:12, padding:14, alignItems:'center' },
  formBtnSecondaryText:{ color:'#374151', fontWeight:'600', fontSize:15 },
  // Option buttons (rule form)
  optBtn:       { borderRadius:20, borderWidth:1, borderColor:'#D1D5DB', paddingHorizontal:12, paddingVertical:7, marginRight:8, backgroundColor:'#F9FAFB' },
  optBtnActive: { backgroundColor:'#0e4e96', borderColor:'#0e4e96' },
  optBtnText:   { fontSize:12, color:'#374151' },
  optBtnTextActive:{ color:'#FFF' },
});

export default AutomationDashboard;
